import type { FC } from 'react';
import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import { SuspenseBoundary } from 'components/common';
import {
  useAdsHistoryFilterOptions,
  useAdsHistoryPacks,
  useUsers,
} from 'features/cabinet/facebook/api';
import AdsHistoryGroupedFilters from 'features/cabinet/facebook/components/AdsHistoryGroupedFilters';
import type { AdsHistoryGroupMode } from 'features/cabinet/facebook/components/AdsHistoryGroupedView';
import AdsHistoryGroupedView from 'features/cabinet/facebook/components/AdsHistoryGroupedView';
import type { AdsHistoryGroupedEntry } from 'features/cabinet/facebook/components/AdsHistoryGroupedView.mockData';
import type { AdsHistoryPacksResponse } from 'features/cabinet/facebook/api/getAdsHistoryPacks';
import useAdsHistoryGroupedParams from 'features/cabinet/facebook/hooks/useAdsHistoryGroupedParams';
import {
  toGroupedFilterParams,
  type AdsHistoryGroupedFilterParams,
  type AdsHistoryGroupedFilterValues,
} from 'features/cabinet/facebook/utils/adsHistoryGroupedFilters';
import { useCheckAuth, UserRole } from 'features/login';
import { usePagination } from 'hooks/usePagination';
import type { MarketingProcess } from 'types/shared';

const MARKETING_PROCESS: MarketingProcess = 'scaling';

const normalizeEntries = (data: AdsHistoryPacksResponse): AdsHistoryGroupedEntry[] =>
  data.data.flatMap((week) =>
    week.packs.flatMap((pack) =>
      pack.ads.map((ad) => ({
        ...ad,
        pack_name: pack.pack_name,
        hypothesis: '',
        email: pack.representative.created_by ?? '',
        created_at: pack.representative.created_at ?? '',
        ad_account: pack.representative.ad_account,
        ad_set: pack.representative.adset,
        message: ad.message ?? pack.representative.message ?? '',
        status: ad.status ?? pack.representative.status,
        ad_id: ad.ad_id ?? '',
      })),
    ),
  );

interface FiltersProps {
  filters: AdsHistoryGroupedFilterValues;
  onFilterChange: (next: AdsHistoryGroupedFilterValues) => void;
  users: string[] | null;
}

const ScalingHistoryByPacksFilters: FC<FiltersProps> = ({ filters, onFilterChange, users }) => {
  const { data: options } = useAdsHistoryFilterOptions({
    params: { marketing_process: MARKETING_PROCESS },
  });

  return (
    <AdsHistoryGroupedFilters
      options={options}
      users={users}
      filters={filters}
      onFilterChange={onFilterChange}
    />
  );
};

// Backend restricts regular users to their own logs, and the users list needs a staff permission.
const ScalingHistoryByPacksFiltersWithUsers: FC<Omit<FiltersProps, 'users'>> = (props) => {
  const { data } = useUsers({ params: { limit: 500, offset: 1 } });
  const users = useMemo(() => data.data.map((u) => u.email).sort(), [data]);

  return <ScalingHistoryByPacksFilters {...props} users={users} />;
};

const ScalingHistoryByPacksGrid: FC<{
  mode: AdsHistoryGroupMode;
  filterParams: AdsHistoryGroupedFilterParams;
  pagination: ReturnType<typeof usePagination>;
}> = ({ mode, filterParams, pagination }) => {
  const { data } = useAdsHistoryPacks({
    params: {
      marketing_process: MARKETING_PROCESS,
      limit: pagination.perPage,
      offset: pagination.page,
      ...filterParams,
    },
  });

  const entries = useMemo(() => normalizeEntries(data), [data]);
  const pageCount = Math.ceil(data.total / data.limit);

  return (
    <>
      <AdsHistoryGroupedView
        key={`${pagination.page}-${pagination.perPage}`}
        initialVisibleGroups={2}
        initialVisibleWeeks={1}
        entries={entries}
        mode={mode}
        emptyPlaceholder="No scaling ads by packs posted recently."
      />
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination
            count={pageCount}
            page={pagination.page}
            onChange={(_, page) => { pagination.handlePageChange(null, page); }}
          />
        </Box>
      )}
    </>
  );
};

const ScalingHistoryByPacksPage: FC = () => {
  const { filters, setFilters } = useAdsHistoryGroupedParams();
  const filterParams = useMemo(() => toGroupedFilterParams(filters), [filters]);
  const pagination = usePagination(Object.values(filterParams), 10);
  const { role } = useCheckAuth();
  const canFilterByUser = role === UserRole.STAFF || role === UserRole.SUPERUSER;

  return (
    <Box sx={{ p: 0 }}>
      <SuspenseBoundary>
        {canFilterByUser ? (
          <ScalingHistoryByPacksFiltersWithUsers filters={filters} onFilterChange={setFilters} />
        ) : (
          <ScalingHistoryByPacksFilters filters={filters} onFilterChange={setFilters} users={null} />
        )}
      </SuspenseBoundary>
      <SuspenseBoundary>
        <ScalingHistoryByPacksGrid
          mode="packs"
          filterParams={filterParams}
          pagination={pagination}
        />
      </SuspenseBoundary>
    </Box>
  );
};

export default ScalingHistoryByPacksPage;
